import { useEffect, useState } from "react";
import {
  api,
  type ConnectedAccount,
  type FacebookPage,
  type PlatformId,
  type PlatformInfo,
} from "../api";
import { PlatformIcon } from "./PlatformIcon";

export function Accounts({
  platforms,
  accounts,
  onChange,
  otherWorkspaceConnections = [],
  resolveWorkspace,
}: {
  platforms: PlatformInfo[];
  accounts: ConnectedAccount[];
  onChange: () => void;
  /** The same user's connections in their OTHER workspaces — used to warn when
   * the exact same social account is already connected elsewhere. */
  otherWorkspaceConnections?: {
    platform: PlatformId;
    accountId: string;
    displayName: string;
    workspaceId: string;
  }[];
  /** Resolve a workspace id to its name for the warning. */
  resolveWorkspace?: (workspaceId: string) => string | null;
}) {
  const byPlatform = new Map(accounts.map((a) => [a.platform, a]));
  // Which platform action is in flight, the surfaced error, and which platform
  // is mid-disconnect-confirm (inline, no native window.confirm — #16).
  const [busyId, setBusyId] = useState<PlatformId | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState<PlatformId | null>(null);

  // Connect OR reconnect: both start the same OAuth flow and redirect away, so a
  // failure to even get the consent URL must be shown, not silently swallowed.
  async function connect(id: PlatformId) {
    setError(null);
    setBusyId(id);
    try {
      const { url } = await api.startConnect(id);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't start the connection.");
      setBusyId(null);
    }
  }

  async function disconnect(id: PlatformId) {
    setError(null);
    setBusyId(id);
    setConfirmingDisconnect(null);
    try {
      await api.disconnect(id);
      onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't disconnect that account.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="card">
      <h2>Connections</h2>
      {error && <p className="error">{error}</p>}
      <ul className="conn-list">
        {platforms.map((p) => {
          const account = byPlatform.get(p.id);
          const needsReconnect = Boolean(account?.needsReconnect);
          const busy = busyId === p.id;
          // Other workspaces of THIS user where the exact same social account is
          // also connected — a soft heads-up against accidental double-posting.
          const alsoIn = account
            ? otherWorkspaceConnections
                .filter((c) => c.platform === p.id && c.accountId === account.accountId)
                .map((c) => (resolveWorkspace ? resolveWorkspace(c.workspaceId) : null))
                .filter((n): n is string => Boolean(n))
            : [];
          return (
            <li key={p.id} className="conn">
              <PlatformIcon platform={p} size={26} />
              <div className="conn-body">
                <div className="conn-name">
                  {p.name}
                  {!p.live && <span className="tag">soon</span>}
                </div>
                <div className="conn-sub">
                  {account ? (
                    needsReconnect ? (
                      <span className="reconnect-pill">
                        <span className="reconnect-dot" aria-hidden="true" />
                        Reconnect needed
                      </span>
                    ) : (
                      <span className="active-pill">
                        <span className="active-dot" aria-hidden="true" />
                        {account.displayName}
                      </span>
                    )
                  ) : (
                    <span className="muted small">
                      {p.configured ? "Not connected" : "Coming soon"}
                    </span>
                  )}
                </div>
                {alsoIn.length > 0 && (
                  <div className="conn-dupe">
                    ⚠ This account is also connected in{" "}
                    <strong>{alsoIn.join(", ")}</strong> — posting from both can double-post.
                  </div>
                )}
                {/* Meta App Review (pages_show_list): once Facebook is connected
                    the operator must see their Pages and pick which one to
                    publish to — and be able to switch later. */}
                {account && p.id === "facebook" && <FacebookPagePicker onChange={onChange} />}
              </div>
              {account ? (
                <div className="conn-actions">
                  {/* An amber "Reconnect needed" account re-runs the same OAuth
                      flow as Connect to refresh its token (#5). */}
                  {needsReconnect && (
                    <button
                      className="btn small warn-btn"
                      disabled={busy}
                      onClick={() => void connect(p.id)}
                    >
                      Reconnect
                    </button>
                  )}
                  {confirmingDisconnect === p.id ? (
                    // Inline confirm affordance instead of a native prompt (#16).
                    <span className="confirm-inline">
                      <button
                        className="ghost small"
                        disabled={busy}
                        onClick={() => void disconnect(p.id)}
                      >
                        Confirm
                      </button>
                      <button
                        className="ghost small"
                        onClick={() => setConfirmingDisconnect(null)}
                      >
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <button
                      className="ghost small"
                      disabled={busy}
                      onClick={() => setConfirmingDisconnect(p.id)}
                    >
                      Disconnect
                    </button>
                  )}
                </div>
              ) : (
                <button
                  className={`btn small ${p.configured ? "" : "disabled"}`}
                  disabled={!p.configured || busy}
                  onClick={() => void connect(p.id)}
                >
                  Connect
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Lists the connected Facebook account's Pages and lets the operator choose
 * which one to publish to. Required by Meta App Review for `pages_show_list`:
 * the user must SEE the full list of Pages they manage and make an EXPLICIT
 * choice — we never auto-select. The chosen Page is what publishing targets, and
 * it can be changed here at any time without reconnecting.
 *
 * Only Page id + name + a publishable flag are ever received here; the per-Page
 * access token stays server-side.
 */
function FacebookPagePicker({ onChange }: { onChange: () => void }) {
  const [pages, setPages] = useState<FacebookPage[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .facebookPages()
      .then((res) => {
        if (cancelled) return;
        setPages(res.pages);
        setSelectedId(res.selectedPageId);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load your Pages.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function choose(pageId: string) {
    setSaving(true);
    setError(null);
    try {
      const { selectedPageId } = await api.selectFacebookPage(pageId);
      setSelectedId(selectedPageId);
      // Refresh the parent so the connection's display name reflects the Page.
      onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not select that Page.");
    } finally {
      setSaving(false);
    }
  }

  if (error && !pages) {
    return <div className="fb-pages error small">{error}</div>;
  }
  if (!pages) {
    return <div className="fb-pages muted small">Loading your Pages…</div>;
  }
  if (pages.length === 0) {
    return (
      <div className="fb-pages muted small">
        This Facebook account manages no Pages. Create a Page (or ask an admin for access), then
        reconnect Facebook.
      </div>
    );
  }

  return (
    <div className="fb-pages">
      <div className="fb-pages-label muted small">
        {selectedId ? "Publishing to this Page" : "Choose which Page to publish to"}
      </div>
      <ul className="fb-page-list">
        {pages.map((page) => {
          const isSelected = page.id === selectedId;
          return (
            <li key={page.id}>
              <label
                className={`fb-page${isSelected ? " on" : ""}${page.canPublish ? "" : " off"}`}
              >
                <input
                  type="radio"
                  name="facebook-page"
                  value={page.id}
                  checked={isSelected}
                  disabled={!page.canPublish || saving}
                  onChange={() => choose(page.id)}
                />
                <span className="fb-page-name">{page.name}</span>
                {!page.canPublish && (
                  <span className="tag">no “Create content” permission</span>
                )}
              </label>
            </li>
          );
        })}
      </ul>
      {error && <div className="error small">{error}</div>}
    </div>
  );
}
